/** @jsx jsx */

import React, {
  Fragment as _,
  memo,
  useMemo,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import { keyframes } from 'emotion';
import { css, jsx } from '@emotion/core';
import { connect } from 'react-redux';
import EditMarker from 'components/UI/EditMarker';
import placeholder from 'assets/addImg@3x.svg';
import { colors, getButtonFocus, focusBoxShadow } from 'styles/variables';
import getBlockById from 'services/apiHelpers/getBlockById';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { EditWrapper0, EditWrapper1 } from './EditWrappers';
import ReactPlayer from 'react-player';
import VideoIcon from 'assets/Video.svg';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { interactionContext } from 'components/lib/TextAndImage/interactionContext';
import { unlockInteraction } from 'services/unlockInteraction';
import onFullScreen from 'services/onFullScreen';
import Transcript from './Transcript';
import onScrollEnds from 'services/onScrollEnds';

const { lPink } = colors;

const frameSelected = keyframes`
  from {
    width: 50%;
    height: 50%;
  }
  to {
    width: 75%;
    height: 75%;
  }
`;

let videoResizeDelay;

const ShowTranscriptBtn = ({ id, setShowTranscript }) => {
  return (
    <button
      id={`show-transcript-${id}`}
      css={css`
        position: absolute;
        left: 50%;
        bottom: -25px;
        // margin: 20px 0 20px 0;
        font-size: 12px;

        transform: translateX(-50%);
        border: none;
        outline: none;
        background: transparent;

        text-decoration: underline;
        cursor: pointer;

        &:focus {
          ${getButtonFocus('#2684FF')}
        }
      `}
      onClick={() => {
        setShowTranscript(true);
      }}
      aria-label="Show transcript"
    >
      Show transcript
    </button>
  );
};

const Video = ({
  id,
  focusedNodeId,
  children,
  ...props
}: {
  id?: any;
  focusedNodeId?: any;
  children?: any;
}) => {
  // const maxWidth = 960;

  const { entry, init } = useIntersectionObserver(true);
  const { src, maxWidth = 960, hideControls, useTranscript } = getBlockById(
    id
  ).props;

  const [isPlaying, setIsPlaying] = useState(false);
  const [calcHeight, setCalcHeight] = useState(maxWidth * 0.5625);
  const [showTranscript, setShowTranscript] = useState(false);

  const selected = focusedNodeId === id;
  const context = useContext(interactionContext);
  const mounted = useRef(false);
  const video = useRef(null);
  const player = useRef(null);

  // const resize = useCallback(() => {
  //   console.log('resize runs');

  //   setCalcHeight(video.current.getBoundingClientRect().width * 0.5625);
  // },[]);

  useLayoutEffect(() => {
    if (!mounted.current) {
      init([video.current], {
        threshold: 0.5,
      });
    }

    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const { width } = video.current.getBoundingClientRect();
    const calcHeightTarget = width * 0.5625;

    if (calcHeight === calcHeightTarget) {
      return;
    }

    setCalcHeight(calcHeightTarget);
  });

  // useEffect(() => {
  //   const resize = () => {
  //     const { width, height } = video.current.getBoundingClientRect();
  //     setCalcHeight(width * 0.5625);
  //   };

  //   const boot = () => {
  //     window.addEventListener('resize', resize);
  //     resize();
  //   };

  //   if (window.dev()) {
  //     boot();
  //   } else {
  //     clearTimeout(videoResizeDelay);
  //     videoResizeDelay = setTimeout(() => {
  //       boot();
  //     }, 300);
  //   }

  //   return () => window.removeEventListener('resize', resize);
  // }, [context?.isOpen]);

  if (entry) {
    if (entry.isIntersecting) {
      onFullScreen(toggle => {
        if (!toggle) {
          const section = document.querySelector(`#i${context.id}`);
          section && section.scrollIntoView();
        }
      });
    } else {
      onFullScreen();
    }
  }

  const reactPlayer = (
    <div
      ref={video}
      css={css`
        position: relative;
        width: 100%;
        max-width: ${maxWidth}px;
        // height: 100%;
        height: ${calcHeight}px;
        margin: 50px 0;
      `}
    >
      <ReactPlayer
        ref={player}
        url={src}
        width="100%"
        height={calcHeight}
        onStart={() => {}}
        onPlay={() => {
          setIsPlaying(true);
        }}
        onPause={() => {
          setIsPlaying(false);
        }}
        onEnded={() => {
          unlockInteraction(context.id);
        }}
        playing={isPlaying && entry && entry.isIntersecting}
        controls={!hideControls}
      />

      {useTranscript && (
        <ShowTranscriptBtn id={id} setShowTranscript={setShowTranscript} />
      )}
    </div>
  );

  return (
    <_>
      {window.dev() ? (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            left: 0px;
            top: 0px;
            width: 100%;
            height: 100%;
          `}
        >
          {reactPlayer}
          {/* {useTranscript && (
            <ShowTranscriptBtn setShowTranscript={setShowTranscript} />
          )} */}
          <div
            css={css`
              position: absolute;
              display: flex;
              justify-content: center;
              align-items: center;
              left: 0px;
              top: 0px;
              width: 100%;
              height: 100%;
              pointer-events: none;
            `}
          >
            <div
              css={css`
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          width: ${selected ? '75%' : '50%'};
          height: ${selected ? '75%' : '50%'};

          ${selected && `animation: ${frameSelected} 0.2s forwards;`}
        
          ${!selected &&
            `
          background: rgba(0, 0, 0, 0.1);
        `}
          
          box-shadow: ${
            selected ? `0 0 0 1px ${lPink}` : '0 0 0 1px lightGrey'
          };
          transition: all 0.2s;
          pointer-events: all;
          cursor: pointer;
          &:hover {
            ${!selected && `box-shadow: 0 0 0 1px ${lPink};`}
          }
        `}
              onClick={e => {
                e.stopPropagation();
                if (selected) return;
                setCurrentBlock({ id });
              }}
            >
              {!selected && (
                <img src={VideoIcon} style={{ width: 50, opacity: 0.5 }} />
              )}

              {!selected && <EditMarker />}
            </div>
          </div>
        </div>
      ) : (
        <div
          css={css`
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
          `}
        >
          {reactPlayer}
          {/* {useTranscript && (
            <ShowTranscriptBtn setShowTranscript={setShowTranscript} />
          )} */}
        </div>
      )}

      {showTranscript && (
        <Transcript
          onClose={() => {
            const tick = setInterval(() => {
              const showTranscriptButtonElement: any = document.querySelector(
                `#show-transcript-${id}`
              );

              if (showTranscriptButtonElement) {
                showTranscriptButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            setShowTranscript(false);
          }}
        >
          {children}
        </Transcript>
      )}
    </_>
  );
};

export default Video;
